/**
 * JWT utilities for extracting permissions from access token
 */

import { jwtDecode } from 'jwt-decode';
import { Permission } from '../../../shared/auth';

/** Groups that indicate admin role (has all permissions) */
const ADMIN_GROUPS = ['Admin', 'admins'];

/** JWT groups that map directly to Permission enum values */
const PERMISSION_GROUPS = Object.values(Permission) as string[];
const MINUTE_MS = 60_000;

export interface JwtPayload {
  sub?: string;
  groups?: string[];
  main_group?: string;
  exp?: number;
  iat?: number;
  inactivityLimit?: number | string;
  sid?: string;
  [key: string]: unknown;
}

/** OIDC id_token claims for session derivation */
export interface IdTokenPayload {
  sub?: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  name?: string;
  preferred_username?: string;
  iat?: number;
  exp?: number;
  [key: string]: unknown;
}

/**
 * Decode OIDC id_token payload (no signature verification - backend validates)
 */
export function decodeIdTokenPayload(idToken: string): IdTokenPayload | null {
  try {
    return jwtDecode<IdTokenPayload>(idToken);
  } catch {
    return null;
  }
}

/**
 * Decode JWT access token payload (no signature verification - backend validates)
 */
export function decodeAccessTokenPayload(
  accessToken: string
): JwtPayload | null {
  try {
    return jwtDecode<JwtPayload>(accessToken);
  } catch {
    return null;
  }
}

/**
 * Extract permissions from JWT groups.
 * Groups that match Permission enum values are returned as permissions.
 */
export function getPermissionsFromGroups(groups: string[] = []): Permission[] {
  const permissions: Permission[] = [];
  for (const group of groups) {
    if (PERMISSION_GROUPS.includes(group)) {
      permissions.push(group as Permission);
    }
  }
  return [...new Set(permissions)];
}

/**
 * Check if user has admin role based on JWT groups
 */
export function isAdminFromGroups(groups: string[] = []): boolean {
  return groups.some(
    (g) => ADMIN_GROUPS.includes(g) || g.toLowerCase().includes('admin')
  );
}

export function getInactivityLimitMs(payload: JwtPayload | null): number | null {
  const raw = payload?.inactivityLimit;
  const minutes =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string'
        ? Number(raw)
        : Number.NaN;

  if (!Number.isFinite(minutes) || minutes <= 0) {
    return null;
  }

  return minutes * MINUTE_MS;
}

export function getIssuedAt(payload: JwtPayload | null): number | null {
  return typeof payload?.iat === 'number' && Number.isFinite(payload.iat)
    ? payload.iat
    : null;
}

export function getStableSessionId(payload: JwtPayload | null): string | null {
  return typeof payload?.sid === 'string' && payload.sid.trim().length > 0
    ? payload.sid.trim()
    : null;
}

export function createSessionKey(
  userId: string,
  stableSessionId: string | null,
  issuedAt: number | null
): string | null {
  if (!userId) {
    return null;
  }

  if (stableSessionId) {
    return `${userId}:${stableSessionId}`;
  }

  if (issuedAt === null) {
    return null;
  }

  return `${userId}:${issuedAt}`;
}
